// components/const/Daily/EquipmentTable.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  getWorkReportDetail,
  getEquipmentUsage,
  getEquipments,
} from "../../../api/const";
import EquipmentNameInput from "./EquipmentNameInput";

function SortableEquipmentRow({
  rowId,
  item,
  idx,
  total,
  allEquip,
  updateRow,
  handleSelect,
  deleteRow,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: rowId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
      {/* 순서 핸들 */}
      <td className="border border-black px-2 py-1 text-center align-middle">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab px-1 py-0.5 border rounded bg-gray-50 active:cursor-grabbing"
        >
          ::
        </button>
      </td>

      {/* 장비명 */}
      <td className="border border-black px-2 py-1">
        <EquipmentNameInput
          value={item.name}
          options={allEquip}
          onInputChange={(v) => updateRow(idx, { name: v })}
          onSelect={(opt) => handleSelect(idx, opt)}
        />
      </td>

      {/* 규격 */}
      <td className="border border-black px-2 py-1">
        <input
          type="text"
          value={item.spec}
          onChange={(e) => updateRow(idx, { spec: e.target.value })}
          className="w-full rounded border px-2 py-1 text-sm"
        />
      </td>

      {/* 전일누계 */}
      <td className="border border-black px-2 py-1 text-center">{item.prev_total}</td>

      {/* 금일장비 */}
      <td className="border border-black px-2 py-1">
        <input
          type="number"
          value={item.today}
          onChange={(e) =>
            updateRow(idx, { today: Number(e.target.value) || 0 })
          }
          className="w-full rounded border px-2 py-1 text-sm"
        />
      </td>

      {/* 누계장비 */}
      <td className="border border-black px-2 py-1 text-center">{total}</td>

      {/* 삭제 */}
      <td className="border border-black px-2 py-1 text-center">
        <button onClick={() => deleteRow(idx)} className="doc-del">
          삭제
        </button>
      </td>
    </tr>
  );
}

export default function EquipmentTable({
  dailyId,
  project,
  date,
  onDataChange,
}) {
  const [data, setData] = useState([
    // ⭐ 기본행 1개 추가됨
    {
      id: null,
      name: "",
      spec: "",
      prev_total: 0,
      today: 0,
    },
  ]);

  const [allEquip, setAllEquip] = useState([]);
  const wrapperRef = useRef();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const getRowId = (row, index) => (row.id != null ? row.id : `row-${index}`);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = data.findIndex(
      (row, index) => getRowId(row, index) === active.id
    );
    const newIndex = data.findIndex(
      (row, index) => getRowId(row, index) === over.id
    );
    if (oldIndex === -1 || newIndex === -1) return;

    const newData = arrayMove(data, oldIndex, newIndex);
    setData(newData);
    onDataChange(newData);
  };

  // 전체 장비 목록 로드
  useEffect(() => {
    getEquipments().then(setAllEquip).catch(console.error);
  }, []);

  // 기존 데이터 로드
  useEffect(() => {
    if (!dailyId || !project || !date) return;
    (async () => {
      try {
        const report = await getWorkReportDetail(dailyId);

        if (!report.equipments.length) return;

        const withPrev = await Promise.all(
          report.equipments.map(async (e) => {
            const usage = await getEquipmentUsage(project, e.id, null, date);
            const sum = usage
              .filter((u) => new Date(u.date) < new Date(date))
              .reduce((acc, u) => acc + (u.qty || 0), 0);

            return {
              id: e.id,
              name: e.name,
              spec: e.spec,
              prev_total: sum,
              today: e.qty,
            };
          })
        );

        setData(withPrev);
        onDataChange(withPrev);
      } catch (err) {
        console.error("장비 데이터 로드 실패:", err);
      }
    })();
  }, [dailyId, project, date]);

  const updateRow = (idx, changes) => {
    setData((prev) => {
      const updated = prev.map((r, i) =>
        i === idx ? { ...r, ...changes } : r
      );
      onDataChange(updated);
      return updated;
    });
  };

  const handleSelect = async (idx, opt) => {
    updateRow(idx, { id: opt.id, name: opt.name, spec: opt.spec });

    try {
      const usage = await getEquipmentUsage(project, opt.id, null, date);
      const sum = usage.reduce((acc, u) => acc + u.qty, 0);
      updateRow(idx, { prev_total: sum });
    } catch (err) {
      console.error(err);
    }
  };

  const addRow = () => {
    const newRow = { id: null, name: "", spec: "", prev_total: 0, today: 0 };
    const updated = [...data, newRow];
    setData(updated);
    onDataChange(updated);
  };

  const deleteRow = (idx) => {
    const updated = data.filter((_, i) => i !== idx);
    setData(updated);
    onDataChange(updated);
  };

  return (
    <div className="" ref={wrapperRef}>
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-body-bold">장비 현황</h3>
      </div>

      {/* 테이블 */}
      <div className="">
        <table className="min-w-full text-sm border-t border-r border-l border-black">
          <colgroup>
            <col style={{ width: "60px" }} /> {/* 순서 */}
            <col style={{ width: "30%" }} /> {/* 장비명 */}
            <col style={{ width: "22%" }} /> {/* 규격 */}
            <col style={{ width: "12%" }} /> {/* 전일누계 */}
            <col style={{ width: "12%" }} /> {/* 금일장비 */}
            <col style={{ width: "12%" }} /> {/* 누계장비 */}
            <col style={{ width: "60px" }} /> {/* 삭제 */}
          </colgroup>
          <thead className="doc-thead border-black">
            <tr>
              <th className="doc-th">순서</th>
              <th className="doc-th">장비명</th>
              <th className="doc-th">규격</th>
              <th className="doc-th">전일누계</th>
              <th className="doc-th">금일장비</th>
              <th className="doc-th">누계장비</th>
              <th className="w-[60px] doc-th-end"></th>
            </tr>
          </thead>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={data.map((row, index) => getRowId(row, index))}
              strategy={verticalListSortingStrategy}
            >
              <tbody className="divide-y divide-black">
                {data.map((item, idx) => {
                  const total = item.prev_total + (item.today || 0);
                  const rowId = getRowId(item, idx);

                  return (
                    <SortableEquipmentRow
                      key={rowId}
                      rowId={rowId}
                      item={item}
                      idx={idx}
                      total={total}
                      allEquip={allEquip}
                      updateRow={updateRow}
                      handleSelect={handleSelect}
                      deleteRow={deleteRow}
                    />
                  );
                })}
              </tbody>
            </SortableContext>
          </DndContext>
        </table>
      </div>
      <div className="flex justify-end">
        <button onClick={addRow} className="btn-basic-sm">
          + 행 추가
        </button>
      </div>
    </div>
  );
}
